/** A chart card for a dashboard band that knows what to draw while its reads are in flight or failed
 * - ADR-0212, package P9.
 *
 * **A failed read draws no chart.** The card stays (a band holds two or three cards, and a missing
 * one would change the page's shape) and says in a few words that it has nothing to show; it is never
 * an empty drawing that reads as "nothing happened" and never zeros. The failure itself, with its
 * retry, is `BandFailures`' to say above the tiles, where it is seen with the charts closed; saying
 * it twice would put two alerts on one problem. While a read is in flight the card shows the shared
 * skeleton. Only when every read has answered does the chart get drawn.
 */

import type { ReactNode } from "react";

import { ChartCard } from "../../components/charts";
import { EmptyState, SkeletonRows } from "../../components/ui";

/** The part of a query result a card needs. */
export interface BandRead {
  error: unknown;
  isPending: boolean;
  refetch: () => unknown;
}

export function BandCard({
  title,
  unit,
  reads,
  hasTable = true,
  action,
  testId,
  children,
}: {
  /** The title, with the unit in `unit`: `Emissions by month`. */
  title: string;
  /** What the numbers are in: `tCO2e`, `documents`. */
  unit?: string;
  /** Every read the drawing depends on. */
  reads: readonly BandRead[];
  hasTable?: boolean;
  action?: ReactNode;
  testId?: string;
  children: ReactNode;
}) {
  const failed = reads.find((read) => Boolean(read.error));
  const pending = !failed && reads.some((read) => read.isPending);
  return (
    <ChartCard
      title={title}
      {...(unit ? { unit } : {})}
      hasTable={!failed && !pending && hasTable}
      {...(action && !failed ? { action } : {})}
      testId={testId ?? "chart-card"}
    >
      {failed ? (
        <EmptyState title="Not shown, its read failed" compact testId="band-card-failed" />
      ) : pending ? (
        <SkeletonRows rows={3} />
      ) : (
        children
      )}
    </ChartCard>
  );
}
